import { EditableFence } from "./types";
import { computeAreaM2, formatArea, normaliseCoordinates } from "./mapUtils";

type SelectedFencePanelProps = {
  fence: EditableFence;
  isBusy: boolean;
  onNameChange: (name: string) => void;
  onSave: (locationId: string) => void;
  onUndo: (locationId: string) => void;
  onDelete: (fence: EditableFence) => void;
  onDeselect: () => void;
};

/** Details and actions for the fence currently selected on the map. */
const SelectedFencePanel = ({
  fence,
  isBusy,
  onNameChange,
  onSave,
  onUndo,
  onDelete,
  onDeselect,
}: SelectedFencePanelProps) => {
  const points = normaliseCoordinates(fence.coordinates).length;
  const area = computeAreaM2(fence.coordinates);

  return (
    <section className="rounded-lg border-2 border-violet-200 bg-violet-50 p-3 shadow">
      <div className="mb-2 flex items-center justify-between">
        <h5 className="text-sm font-bold text-gray-900">Selected fence</h5>
        <button
          type="button"
          onClick={onDeselect}
          className="text-xs text-gray-600 underline hover:text-gray-900"
          title="Press Escape to deselect"
        >
          Deselect (Esc)
        </button>
      </div>

      <label className="block text-xs font-medium text-gray-700" htmlFor="fence-name">
        Name
      </label>
      <input
        id="fence-name"
        value={fence.name}
        type="text"
        placeholder="e.g. Main entrance"
        autoFocus={fence.status === "NEW"}
        className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm focus:border-violet-500 focus:outline-none"
        onChange={(e) => onNameChange(e.target.value)}
      />

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-700">
        <dt>Points</dt>
        <dd className="text-right font-medium">{points}</dd>
        <dt>Area</dt>
        <dd className="text-right font-medium">{area === null ? "—" : formatArea(area)}</dd>
        <dt>Status</dt>
        <dd className="text-right font-medium">{fence.status === "ACTIVE" ? "Saved" : fence.status === "NEW" ? "New" : "Edited"}</dd>
      </dl>

      <div className="mt-3 flex items-center gap-2">
        {fence.status !== "ACTIVE" && (
          <>
            <button
              type="button"
              onClick={() => onSave(fence.locationId)}
              disabled={isBusy}
              className="rounded bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBusy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => onUndo(fence.locationId)}
              disabled={isBusy}
              className="rounded bg-gray-500 px-4 py-2 text-sm font-bold text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Undo
            </button>
          </>
        )}
        {fence.status !== "NEW" && (
          <button
            type="button"
            onClick={() => onDelete(fence)}
            disabled={isBusy}
            className="ml-auto rounded bg-red-500 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Delete fence
          </button>
        )}
      </div>
    </section>
  );
};

export default SelectedFencePanel;
